import React, { useState, useEffect, useCallback, useRef } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import InputBase from "@material-ui/core/InputBase";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import SearchIcon from "@material-ui/icons/Search";
import RefreshIcon from "@material-ui/icons/Refresh";

import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles(theme => ({
  page: { height: "100%", overflowY: "auto", background: theme.app.chatBg },
  wrap: { width: "100%", maxWidth: 1040, margin: "0 auto", padding: theme.spacing(3.5, 3, 6) },
  header: { display: "flex", alignItems: "flex-start", gap: 12, marginBottom: theme.spacing(2) },
  title: { fontWeight: 600, fontSize: 18, color: theme.app.ink },
  subtitle: { color: theme.app.textTertiary, fontSize: 13, marginTop: 4 },
  grow: { flexGrow: 1 },
  toolbar: { display: "flex", alignItems: "center", gap: 8, marginBottom: theme.spacing(1.5) },
  search: { display: "flex", alignItems: "center", gap: 6, width: 240, height: 34, padding: "0 10px", border: `1px solid ${theme.app.border}`, borderRadius: 8, background: "#fff", "& svg": { fontSize: 17, color: theme.app.textTertiary } },
  input: { fontSize: 13, flex: 1 },
  filters: { display: "flex", gap: 4 },
  fbtn: { fontSize: 12, height: 30, padding: "0 12px", borderRadius: 7, border: `1px solid ${theme.app.border}`, background: "#fff", color: theme.app.textSecondary, cursor: "pointer", fontFamily: theme.app.mono },
  fbtnOn: { background: theme.app.dark, color: "#fff", borderColor: theme.app.dark },
  tbl: { background: "#fff", border: `1px solid ${theme.app.border}`, borderRadius: 10, overflow: "hidden" },
  headRow: { display: "grid", gridTemplateColumns: "150px 60px 1fr 64px 80px 150px", gridColumnGap: 16, height: 38, alignItems: "center", padding: "0 16px", borderBottom: `1px solid ${theme.app.border}` },
  hc: { fontSize: 11, textTransform: "uppercase", letterSpacing: "0.05em", color: theme.app.textTertiary, fontWeight: 600 },
  row: { display: "grid", gridTemplateColumns: "150px 60px 1fr 64px 80px 150px", gridColumnGap: 16, minHeight: 44, alignItems: "center", padding: "0 16px", borderBottom: `1px solid ${theme.app.lineSoft}`, cursor: "pointer", "&:hover": { background: theme.app.hover }, "&:last-child": { borderBottom: "none" } },
  time: { fontFamily: theme.app.mono, fontSize: 12, color: theme.app.textSecondary },
  method: { fontFamily: theme.app.mono, fontSize: 11, fontWeight: 700, color: theme.app.textSecondary },
  path: { fontFamily: theme.app.mono, fontSize: 12.5, color: theme.app.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  status: { fontFamily: theme.app.mono, fontSize: 11.5, fontWeight: 600, padding: "1px 7px", borderRadius: 4, justifySelf: "start" },
  st2: { background: "#E6F4EC", color: theme.app.ok },
  st4: { background: "#FBEFE0", color: theme.app.warn },
  st5: { background: "#F7E3E1", color: theme.app.danger },
  lat: { fontFamily: theme.app.mono, fontSize: 12, color: theme.app.textTertiary, textAlign: "right" },
  key: { fontFamily: theme.app.mono, fontSize: 11.5, color: theme.app.textSecondary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  detail: { gridColumn: "1 / -1", padding: "10px 0 4px", borderTop: `1px dashed ${theme.app.border}`, marginTop: 8 },
  dl: { display: "grid", gridTemplateColumns: "120px 1fr", gap: "4px 12px", fontSize: 12.5 },
  dk: { color: theme.app.textTertiary },
  dv: { fontFamily: theme.app.mono, color: theme.app.ink, wordBreak: "break-all" },
  err: { color: theme.app.danger },
  empty: { padding: theme.spacing(6, 2), textAlign: "center", color: theme.app.textTertiary, fontSize: 13 },
}));

const STATUS_FILTERS = [
  { key: "", label: "Tudo" },
  { key: "2xx", label: "2xx" },
  { key: "4xx", label: "4xx" },
  { key: "5xx", label: "5xx" },
  { key: "error", label: "Erros" },
];

const fmtTime = d => {
  try {
    return new Date(d).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit" });
  } catch (e) {
    return "—";
  }
};

const ApiLogs = () => {
  const classes = useStyles();
  const [logs, setLogs] = useState([]);
  const [count, setCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(null); // id expandido
  const qTimer = useRef(null);

  const fetchPage = useCallback(async (pageNumber, st, query, append) => {
    try {
      const { data } = await api.get("/developer/api-logs", { params: { pageNumber, status: st, q: query } });
      setLogs(prev => (append ? [...prev, ...(data.logs || [])] : data.logs || []));
      setCount(data.count || 0);
      setHasMore(!!data.hasMore);
    } catch (err) {
      toastError(err);
    }
  }, []);

  useEffect(() => {
    setPage(1);
    fetchPage(1, status, q, false);
  }, [status, q, fetchPage]);

  const onSearch = v => {
    clearTimeout(qTimer.current);
    qTimer.current = setTimeout(() => setQ(v), 350);
  };

  const loadMore = () => {
    const next = page + 1;
    setPage(next);
    fetchPage(next, status, q, true);
  };

  const refresh = () => fetchPage(1, status, q, false);

  const statusClass = s => (s >= 500 ? classes.st5 : s >= 400 ? classes.st4 : classes.st2);

  return (
    <div className={classes.page}>
      <div className={classes.wrap}>
        <div className={classes.header}>
          <div>
            <Typography className={classes.title}>Logs da API</Typography>
            <Typography className={classes.subtitle}>
              Toda requisição às chaves de API (<code>/v1</code>). Use o <code>X-Request-Id</code> retornado para correlacionar com seus logs.
            </Typography>
          </div>
          <div className={classes.grow} />
          <IconButton size="small" onClick={refresh} title="Atualizar">
            <RefreshIcon fontSize="small" />
          </IconButton>
        </div>

        <div className={classes.toolbar}>
          <div className={classes.search}>
            <SearchIcon />
            <InputBase className={classes.input} placeholder="Filtrar por caminho…" defaultValue={q} onChange={e => onSearch(e.target.value)} />
          </div>
          <div className={classes.filters}>
            {STATUS_FILTERS.map(f => (
              <div key={f.key} className={`${classes.fbtn} ${status === f.key ? classes.fbtnOn : ""}`} onClick={() => setStatus(f.key)}>
                {f.label}
              </div>
            ))}
          </div>
          <div className={classes.grow} />
          <Typography className={classes.time}>{count} requisições</Typography>
        </div>

        <div className={classes.tbl}>
          <div className={classes.headRow}>
            <span className={classes.hc}>Quando</span>
            <span className={classes.hc}>Método</span>
            <span className={classes.hc}>Caminho</span>
            <span className={classes.hc}>Status</span>
            <span className={classes.hc} style={{ textAlign: "right" }}>Latência</span>
            <span className={classes.hc}>Chave</span>
          </div>
          {logs.length === 0 ? (
            <div className={classes.empty}>Nenhuma requisição ainda. Chame <code>/v1</code> com uma chave de API para ver os logs aqui.</div>
          ) : (
            logs.map(l => (
              <div key={l.id} className={classes.row} onClick={() => setOpen(open === l.id ? null : l.id)}>
                <span className={classes.time}>{fmtTime(l.createdAt)}</span>
                <span className={classes.method}>{l.method}</span>
                <span className={classes.path}>{l.path}</span>
                <span className={`${classes.status} ${statusClass(l.statusCode)}`}>{l.statusCode}</span>
                <span className={classes.lat}>{l.latencyMs}ms</span>
                <span className={classes.key}>{l.keyName || "—"}</span>
                {open === l.id && (
                  <div className={classes.detail}>
                    <div className={classes.dl}>
                      <span className={classes.dk}>Request ID</span>
                      <span className={classes.dv}>{l.requestId || "—"}</span>
                      <span className={classes.dk}>Escopo</span>
                      <span className={classes.dv}>{l.scope || "—"}</span>
                      <span className={classes.dk}>IP</span>
                      <span className={classes.dv}>{l.ip || "—"}</span>
                      {l.errorMessage && (
                        <>
                          <span className={classes.dk}>Erro</span>
                          <span className={`${classes.dv} ${classes.err}`}>{l.errorMessage}</span>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        {hasMore && (
          <div style={{ textAlign: "center", marginTop: 16 }}>
            <Button variant="outlined" color="secondary" onClick={loadMore}>
              Carregar mais
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ApiLogs;
